import { useCallback, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, FlaskConical } from 'lucide-react';
import { getStaffLandingPath } from '../lib/staffLanding';
import { LOGO_MARK } from '../lib/brand';
import { LoginLockNotice } from '../components/LoginLockNotice';
import api from '../lib/api';
import { getLastSignin, saveLastSignin, forgetLastSignin } from '../lib/desktopMemory';
import { quickSigninSupported } from '../lib/quickSignin';
import { QuickPinSignIn } from '../components/QuickPinSignIn';
import { useDemoConfig } from '../lib/demoConfig';


export default function LoginPage() {
  const { user, loading: restoringSession, login, loginWithGoogle, switchBranch } = useAuth();
  const demo = useDemoConfig();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [lastSignin, setLastSignin] = useState(() => getLastSignin());
  const [loginData, setLoginData] = useState(() => ({ email: getLastSignin()?.email || '', password: '' }));
  const [attemptsLeft, setAttemptsLeft] = useState(null);
  const [lockedUntil, setLockedUntil] = useState(null);
  const clearLock = useCallback(() => setLockedUntil(null), []);
  const [resetSent, setResetSent] = useState(false);

  const requestReset = async () => {
    try {
      await api.post('/auth/forgot-password-request', { email: loginData.email });
      setResetSent(true);
    } catch {
      toast.error('Could not send the request — please try again');
    }
  };

  // Returns true when a bad password / lockout is shown inline instead of a toast.
  const handleAuthFailure = (err) => {
    const status = err.response?.status;
    const headers = err.response?.headers || {};
    if (status === 429) {
      const secs = parseInt(headers['retry-after'], 10) || 900;
      setAttemptsLeft(null);
      setLockedUntil(Date.now() + secs * 1000);
      return true;
    }
    if (status === 401 && headers['x-attempts-left'] != null) {
      setAttemptsLeft(parseInt(headers['x-attempts-left'], 10));
      return true;
    }
    return false;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await login(loginData.email, loginData.password);
      await finishSignIn(userData, loginData.email);
    } catch (err) {
      if (!handleAuthFailure(err)) toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const [quickMode, setQuickMode] = useState(() => quickSigninSupported());
  const leaveQuickMode = useCallback(() => setQuickMode(false), []);

  const finishSignIn = async (userData, email) => {
    const sameUser = lastSignin?.email?.toLowerCase() === email.trim().toLowerCase();
    const branch = sameUser ? lastSignin?.branch : null;
    saveLastSignin({ email: email.trim(), branch: branch || null });
    if (branch?.tenant_id) await switchBranch(branch.tenant_id, branch.name);
    toast.success(branch?.name ? `Welcome back — opened ${branch.name}` : 'Welcome back!');
    navigate(getStaffLandingPath(userData), { replace: true });
  };

  if (restoringSession) return (
    <div className="min-h-screen flex items-center justify-center gap-3" data-testid="login-restoring-session" role="status">
      <Loader2 className="h-5 w-5 animate-spin" /> Opening your session…
    </div>
  );
  if (user) return <Navigate to={getStaffLandingPath(user)} replace />;

  return (
    <div className="min-h-screen flex flex-col" data-testid="login-page">

      <div className="flex flex-1">
        {/* Left Side - Image */}
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-[#0A4D3C]">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/498807ae-94a0-4d6a-95f0-a5cea3345feb/images/2ab2b9fe1e37f6591f42a64478b73ff2420dfc2735a773478ef3c33903d4b2c3.jpeg"
            alt="Pharmacists with dispensing robots"
            className="object-cover w-full h-full opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A4D3C] via-[#0A4D3C]/60 to-transparent" />
          <div className="absolute bottom-16 left-12 right-12 max-w-lg">
            <div className="flex items-center gap-3 mb-6">
              <img src={LOGO_MARK} alt="" aria-hidden="true"
                className="h-11 w-11 rounded-xl border border-white/20 object-cover" />
              <h1 className="text-4xl font-bold tracking-tight text-white leading-none" style={{ fontFamily: 'Outfit, sans-serif' }}>
                DutiLyne
                <span className="block text-[11px] font-medium uppercase tracking-[0.22em] text-white/70 mt-1.5">Pharmacy POS</span>
              </h1>
            </div>
            <p className="text-lg text-white/80 leading-relaxed font-light">
              Closer to your patients, Connected to your Pharmacy.
            </p>
            <div className="flex gap-6 mt-8">
              <div className="text-center">
                <p className="text-2xl font-bold text-white">500+</p>
                <p className="text-xs text-white/60 tracking-wide uppercase">Pharmacies</p>
              </div>
              <div className="w-px bg-white/20"></div>
              <div className="text-center">
                <p className="text-2xl font-bold text-white">99.9%</p>
                <p className="text-xs text-white/60 tracking-wide uppercase">Uptime</p>
              </div>
              <div className="w-px bg-white/20"></div>
              <div className="text-center">
                <p className="text-2xl font-bold text-white">24/7</p>
                <p className="text-xs text-white/60 tracking-wide uppercase">Support</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-background">
          <div className="w-full max-w-md animate-fade-in-up">
            {/* Mobile logo */}
            <div className="lg:hidden mb-8" data-testid="login-wordmark">
              <img src="/brand/dutilyne-lockup-regular.webp" alt="DutiLyne Pharmacy POS" className="h-12 object-contain" />
            </div>
            <p className="lg:hidden text-sm text-primary font-medium -mt-5 mb-7" data-testid="login-tagline">
              Closer to your patients, Connected to your Pharmacy.
            </p>

            <Card className="border-border shadow-sm">
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Welcome
                </CardTitle>
                <CardDescription>
                  Sign in to your pharmacy management system
                </CardDescription>
              </CardHeader>
              <CardContent>
                {quickMode && (
                  <QuickPinSignIn onSignedIn={finishSignIn} onEmpty={leaveQuickMode}
                    onUsePassword={(email) => { setQuickMode(false); setLoginData({ email: email || '', password: '' }); }} />
                )}
                {!quickMode && (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    {lastSignin?.email && (
                      <div className="flex items-center justify-between gap-2 rounded-lg border border-primary/25 bg-primary/5 px-3 py-2 text-xs"
                        data-testid="login-last-signin">
                        <span className="min-w-0 truncate">
                          Welcome back
                          {lastSignin.branch?.name && <> — signing in to <b data-testid="login-last-branch">{lastSignin.branch.name}</b></>}
                        </span>
                        <button type="button" className="shrink-0 font-semibold text-primary hover:underline" data-testid="login-not-you-btn"
                          onClick={() => { forgetLastSignin(); setLastSignin(null); setLoginData({ email: '', password: '' }); }}>
                          Not you?
                        </button>
                      </div>
                    )}
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="pharmacist@example.com"
                      value={loginData.email}
                      onChange={(e) => { setLoginData(p => ({ ...p, email: e.target.value })); setAttemptsLeft(null); setLockedUntil(null); setResetSent(false); }}
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="Enter your password"
                      value={loginData.password}
                      onChange={(e) => setLoginData(p => ({ ...p, password: e.target.value }))}
                      required
                      data-testid="login-password-input"
                    />
                  </div>
                  <LoginLockNotice attemptsLeft={attemptsLeft} lockedUntil={lockedUntil} onUnlock={clearLock} onForgot={requestReset} resetSent={resetSent} />
                  <Button type="submit" className="w-full h-10" disabled={loading || !!lockedUntil} data-testid="login-submit-btn">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Sign In
                  </Button>
                </form>
                )}

                <div className="my-5 flex items-center gap-3">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-[11px] uppercase tracking-wider text-muted-foreground">or</span>
                  <div className="h-px flex-1 bg-border" />
                </div>

                {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-10 gap-2.5 font-medium"
                  onClick={loginWithGoogle}
                  data-testid="login-google-btn"
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
                    <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.7 4.1-5.5 4.1-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.7 3.3 14.6 2.3 12 2.3 6.7 2.3 2.4 6.6 2.4 12s4.3 9.7 9.6 9.7c5.5 0 9.2-3.9 9.2-9.4 0-.6-.1-1.1-.2-1.6L12 10.2z"/>
                    <path fill="#4285F4" d="M21 12.3c0-.6-.1-1.1-.2-1.6H12v3.9h5.1c-.2 1.2-.9 2.2-1.9 2.9v2.4h3.1c1.8-1.7 2.7-4 2.7-7.6z"/>
                    <path fill="#FBBC05" d="M6.5 14.1c-.2-.5-.3-1-.3-1.6 0-.6.1-1.1.3-1.6V8.6H3.4C2.8 9.6 2.4 10.7 2.4 12s.4 2.4 1 3.4l3.1-1.3z"/>
                    <path fill="#34A853" d="M12 21.7c2.6 0 4.7-.9 6.3-2.3l-3.1-2.4c-.9.6-2 .9-3.2.9-2.5 0-4.6-1.7-5.3-4l-3.1 1.3c1.5 3 4.6 5.5 8.4 5.5z"/>
                  </svg>
                  Continue with Google
                </Button>

                <p className="text-xs text-center text-muted-foreground mt-5">
                  Contact your administrator if you need an account
                </p>
                <div className="mt-3 text-center">
                  <Link to="/signup/pharmacy" className="text-sm font-semibold text-primary hover:underline" data-testid="login-pharmacy-signup-link">Create a pharmacy workspace</Link>
                </div>

                {demo.enabled && (
                  <div className="mt-3 text-center">
                    <Link to="/try-demo" className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-primary" data-testid="login-try-demo-link">
                      <FlaskConical className="h-3.5 w-3.5" /> Try the demo pharmacy — sample data, no account needed
                    </Link>
                  </div>
                )}

                <div className="mt-4 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  <span>HIPAA-ready secure platform</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
